import { useEffect, useMemo, useState } from 'react';
import LogoMark from '../components/LogoMark';
import { apiFetch } from '../config/api';
import styles from './StaffRegister.module.css';

// Same values the deploy injects into every other surface; the literals are the
// fallback for a local build with no env file.
const STAFF_APP_STORE_URL = process.env.REACT_APP_STAFF_APP_APPSTORE_LINK
  || 'https://apps.apple.com/us/app/car-pool-staff/id6762563234';
const STAFF_PLAY_STORE_URL = process.env.REACT_APP_STAFF_APP_PLAYSTORE_LINK
  || 'https://play.google.com/store/apps/details?id=com.nativ.carpool.staff';

function getSchoolName(school) {
  return school?.school_nickname || school?.legal_name || 'your school';
}

function sortSchools(left, right) {
  return getSchoolName(left).localeCompare(getSchoolName(right));
}

export default function StaffRegister({ onBack, schoolId = '' }) {
  const initialSchoolId = schoolId ? String(schoolId) : '';
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    schoolId: initialSchoolId,
    positionTitle: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [schools, setSchools] = useState([]);
  const [school, setSchool] = useState(null);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const schoolLocked = Boolean(initialSchoolId);
  const selectedSchool = useMemo(() => {
    if (school) return school;
    return schools.find((item) => String(item.id) === String(form.schoolId)) || null;
  }, [form.schoolId, school, schools]);

  useEffect(() => {
    let cancelled = false;

    async function loadSchools() {
      setLoadingSchools(true);
      setError('');

      try {
        if (initialSchoolId) {
          const res = await apiFetch(`/schools/${initialSchoolId}`);
          if (!cancelled) {
            setSchool(res.data);
            setForm((current) => ({ ...current, schoolId: String(res.data.id) }));
          }
          return;
        }

        const res = await apiFetch('/schools');
        if (!cancelled) {
          const activeSchools = (res.data || [])
            .filter((item) => ['ACTIVE', 'TRIAL'].includes(item.is_active))
            .sort(sortSchools);
          setSchools(activeSchools);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load schools.');
      } finally {
        if (!cancelled) setLoadingSchools(false);
      }
    }

    loadSchools();
    return () => { cancelled = true; };
  }, [initialSchoolId]);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.schoolId) {
      setError('Select your school.');
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await apiFetch('/staff/registration-requests/public', {
        method: 'POST',
        body: JSON.stringify({
          school_id: Number(form.schoolId),
          f_name: form.firstName.trim(),
          l_name: form.lastName.trim(),
          school_email: form.email.trim(),
          mobile_phone: form.phone.trim(),
          position_title: form.positionTitle.trim() || null,
          password: form.password,
        }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles.page}>
        <div className={`glass-card ${styles.card}`}>
          <div className={styles.logoRow}>
            <LogoMark size={26} />
            <span className={styles.logoText}>car-pool</span>
          </div>
          <h2 className={styles.successTitle}>Request Submitted</h2>
          <p className={styles.successSub}>
            Your staff account request for {getSchoolName(selectedSchool)} is now waiting for administrator review.
            Once approved, you can sign in with the email and password you entered.
          </p>
          <div className={styles.checkCircle}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          {/* Getting the app installed while they wait is the one useful thing
              left to do here — approval can take a day, and downloading it
              later means finding this page again. */}
          <div className={styles.appPromo}>
            <p className={styles.appPromoTitle}>Get the Car-Pool Staff app</p>
            <p className={styles.appPromoSub}>
              Install it now and sign in with the same email and password. Until an administrator
              approves you, the app will simply say your account is pending.
            </p>
            <div className={styles.appPromoButtons}>
              <a
                className={styles.appPromoBtn}
                href={STAFF_APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Download for iPhone
              </a>
              <a
                className={styles.appPromoBtn}
                href={STAFF_PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Download for Android
              </a>
            </div>
          </div>

          <button className={styles.btnDark} style={{ marginTop: '1.25rem' }} onClick={onBack}>
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`glass-card ${styles.card}`}>
        <div className={styles.logoRow}>
          <LogoMark size={26} />
          <span className={styles.logoText}>car-pool</span>
        </div>

        <h1 className={styles.heading}>Request Staff Access</h1>
        <p className={styles.sub}>
          {schoolLocked
            ? `Create an account request for ${getSchoolName(selectedSchool)}.`
            : "Create an account request for your school's Car-Pool system."}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.label}>First name</span>
              <input
                className={styles.input}
                type="text"
                value={form.firstName}
                onChange={set('firstName')}
                required
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Last name</span>
              <input
                className={styles.input}
                type="text"
                value={form.lastName}
                onChange={set('lastName')}
                required
              />
            </label>
          </div>

          <label className={styles.field}>
            <span className={styles.label}>School</span>
            {schoolLocked ? (
              <div className={styles.readonlyValue}>
                {loadingSchools ? 'Loading school...' : getSchoolName(selectedSchool)}
              </div>
            ) : (
              <div className={styles.selectWrapper}>
                <select
                  className={styles.select}
                  value={form.schoolId}
                  onChange={set('schoolId')}
                  disabled={loadingSchools}
                  required
                >
                  <option value="" disabled>{loadingSchools ? 'Loading schools...' : 'Select school'}</option>
                  {schools.map((item) => (
                    <option key={item.id} value={item.id}>{getSchoolName(item)}</option>
                  ))}
                </select>
                <span className={styles.selectArrow}>&#8964;</span>
              </div>
            )}
          </label>

          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.label}>Position title</span>
              <input
                className={styles.input}
                type="text"
                placeholder="Teacher, dismissal lead, office staff"
                value={form.positionTitle}
                onChange={set('positionTitle')}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Mobile phone</span>
              <input
                className={styles.input}
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                required
              />
            </label>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Initial access</span>
            <div className={styles.readonlyValue}>
              <strong>Carpool Viewer</strong> — you can monitor dismissal activity, but only a school administrator can grant Carpool Manager controls.
            </div>
          </div>

          <label className={styles.field}>
            <span className={styles.label}>School work email</span>
            <input
              className={styles.input}
              type="email"
              placeholder="you@school.com"
              value={form.email}
              onChange={set('email')}
              required
            />
          </label>

          <div className={styles.row}>
            <div className={styles.passwordWrapper}>
              <input
                className={styles.input}
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={form.password}
                onChange={set('password')}
                minLength={8}
                required
              />
              <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword((value) => !value)}>
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
            <div className={styles.passwordWrapper}>
              <input
                className={styles.input}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm password"
                value={form.confirmPassword}
                onChange={set('confirmPassword')}
                minLength={8}
                required
              />
              <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirm((value) => !value)}>
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>

          <button type="submit" className={styles.btnDark} disabled={loading || loadingSchools}>
            {loading ? 'Submitting...' : 'Submit Staff Request'}
          </button>
        </form>

        <p className={styles.loginLink}>
          Already have an account?{' '}
          <button type="button" className={styles.linkBtn} onClick={onBack}>
            Return to login
          </button>
        </p>
      </div>
    </div>
  );
}

function Eye() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOff() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
